import {useQuery} from '@tanstack/react-query';

import * as ApplicationsService from '../../applications/services/ApplicationsService';
import {
    Application,
    ApplicationStatus,
} from '../../applications/types';

// FNP-979. Shared resolution of console-api `Application` records for the two
// testing catalogs (Actions, Test Definitions), which reference an application
// by `applicationId` — a FOREIGN KEY into console-api, not a local entity.
//
// This deliberately reuses `applications/services/ApplicationsService`, which
// goes through the DEFAULT Amplify client (console-api). The testing API does
// not own applications and must never be asked for them, so there is no third
// client here and no listApplications in the testing-api integration.
//
// ⚠️ UNFILTERED ON PURPOSE. `useApplicationsInfiniteQuery` defaults its filter
// to `applicationStatus: ACTIVE`, which is right for the applications catalog
// and WRONG here: a definition may legitimately reference an application that
// has since gone INACTIVE, and that row still has to render its name and stay
// selectable in the form. Filtering to ACTIVE would make the existing selection
// unresolvable — see `useApplicationOptions` below.
export const applicationOptionsKeys = {
    all: ['applications', 'options'] as const,
};

// One page of 200 is enough for the application catalog at its current size and
// keeps this a single round-trip. If it ever outgrows that, page it here rather
// than reintroducing a status filter — the point of this hook is that a
// referenced application always resolves.
const OPTIONS_PAGE_SIZE = 200;

export const useApplicationsForReferenceQuery = () => {
    return useQuery({
        queryKey: applicationOptionsKeys.all,
        queryFn: () => ApplicationsService.fetchRecords({}, OPTIONS_PAGE_SIZE),
        // The catalog changes rarely relative to a form session; this keeps the
        // Select from refetching every time a drawer opens.
        staleTime: 5 * 60 * 1000,
    });
};

export type ApplicationOption = {
    value: string,
    label: string,
    // True when the application has no usable `url`, so the executor cannot
    // navigate to it. Surfaced at authoring time rather than discovered as a
    // runtime APPLICATION_URL_MISSING / APPLICATION_URL_UNREACHABLE.
    unnavigable: boolean,
    // Why it is unnavigable — shown in the option label and the alert.
    unnavigableReason?: string,
};

// An application the executor cannot reach. Verified against live dev data
// (2026-08-15): `flexinet-contacts-app` has `url: null`, and "Admin Console
// Localhost" has `url: https://localhost:8012`. The executor fails OPEN on both
// (APPLICATION_URL_MISSING / APPLICATION_URL_UNREACHABLE), so these stay
// SELECTABLE — the operator is only warned, never blocked.
//
// ⚠️ Reads `url`, NOT `stageDomains[stage]`. `stageDomains` is what the
// provisioner INTENDS to build; `url` is what is actually live. The testing-api
// schema calls this out explicitly and the executor resolves `url`.
const describeUnnavigable = (application: Application): string | undefined => {
    const url = application.url?.trim();

    if (!url) {
        return 'no URL';
    }

    // A loopback host resolves on the authoring machine and nowhere else, so an
    // executor running in Lambda can never reach it.
    if (/^https?:\/\/(localhost|127\.0\.0\.1|\[::1\])(:|\/|$)/i.test(url)) {
        return 'localhost URL';
    }

    return undefined;
};

const labelFor = (application: Application): string => {
    // Applications are not guaranteed to have a name; the id is the fallback so
    // an option is never blank.
    const name = application.displayName?.trim() ||
        application.name?.trim() ||
        application.id;

    const suffixes: string[] = [];

    if (application.applicationStatus &&
        application.applicationStatus !== ApplicationStatus.ACTIVE) {
        suffixes.push(application.applicationStatus);
    }

    const unnavigableReason = describeUnnavigable(application);
    if (unnavigableReason) {
        suffixes.push(unnavigableReason);
    }

    return suffixes.length > 0 ? `${name} (${suffixes.join(', ')})` : name;
};

// Builds the Select options and resolves a display name for a given id.
//
// ⚠️ THE SELECTED VALUE IS ALWAYS PRESENT IN `options`. Mantine's Select
// silently drops a value with no matching option, so an application that has
// been archived (or that this operator cannot see) would vanish from the form
// and then be ERASED on the next save — a destructive edit nobody asked for.
// Same defence as testing-personas/forms/TestDefinitionsField.tsx.
export const useApplicationOptions = (selectedId?: string | null) => {
    const {
        data,
        isLoading,
        isError,
    } = useApplicationsForReferenceQuery();

    const applications: Application[] = data?.items ?? [];

    const options: ApplicationOption[] = applications.map(application => {
        const unnavigableReason = describeUnnavigable(application);
        return {
            value: application.id,
            label: labelFor(application),
            unnavigable: unnavigableReason !== undefined,
            unnavigableReason,
        };
    });

    const selected = selectedId?.trim() ? selectedId.trim() : undefined;

    // The dangling case: a referenced application that the list did not return.
    // It is labelled as unresolved rather than hidden, so the operator can see
    // WHAT the record points at instead of an empty field. Not added while the
    // list is still loading, or every edit form would flash a false "unresolved".
    if (selected && !isLoading &&
        !options.some(option => option.value === selected)) {
        options.push({
            value: selected,
            label: `${selected} (unresolved)`,
            unnavigable: true,
            unnavigableReason: 'application not found',
        });
    }

    const selectedOption = selected
        ? options.find(option => option.value === selected)
        : undefined;

    return {
        options,
        selectedOption,
        isLoading,
        isError,
    };
};

// Returns a LOOKUP FUNCTION rather than a single name, so a caller that has to
// resolve many ids (a keyword filter running over a whole table) subscribes to
// the query once instead of once per row — a hook cannot be called in a loop.
//
// Falling back to the id (rather than blank/undefined) keeps a dangling
// reference VISIBLE: an empty value reads as "no application", which is a
// different and misleading fact.
export const useApplicationNameResolver = () => {
    const {data} = useApplicationsForReferenceQuery();

    const applications = data?.items ?? [];

    return (applicationId?: string | null): string | undefined => {
        if (!applicationId) {
            return undefined;
        }

        const application = applications.find(item => item.id === applicationId);

        // Unresolved (still loading, archived, or not visible to this operator)
        // — the raw id is the honest thing to show.
        if (!application) {
            return applicationId;
        }

        return application.displayName?.trim() ||
            application.name?.trim() ||
            applicationId;
    };
};

// Display helper for a SINGLE reference (profiles, table cells).
export const useApplicationName = (applicationId?: string | null) => {
    return useApplicationNameResolver()(applicationId);
};
